import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { BaseModal } from '../ui/BaseModal';
import { FormInput } from '../ui/FormInput';
import { useRenameNode } from '../../services/documents.service';

interface RenameNodeModalProps {
  companyId: string;
}

export interface RenameNodeModalRef {
  open: (node: DocumentNodeWithDetailsDto) => void;
}

interface RenameFormData {
  name: string;
}

export const RenameNodeModal = forwardRef<RenameNodeModalRef, RenameNodeModalProps>(
  ({ companyId }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState<DocumentNodeWithDetailsDto | null>(null);

    const {
      register,
      handleSubmit: handleFormSubmit,
      formState: { errors },
      reset,
      setValue,
    } = useForm<RenameFormData>({
      defaultValues: { name: '' },
    });

    const renameNodeMutation = useRenameNode(companyId);

    useImperativeHandle(ref, () => ({
      open: (node: DocumentNodeWithDetailsDto) => {
        setSelectedNode(node);
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (isOpen && selectedNode) {
        setValue('name', selectedNode.name);
      } else {
        reset();
      }
    }, [isOpen, selectedNode, setValue, reset]);

    const handleSubmit = (data: RenameFormData) => {
      if (!selectedNode) return;

      renameNodeMutation.mutate(
        { nodeId: selectedNode.id, data: { name: data.name } },
        { onSuccess: handleClose },
      );
    };

    const handleClose = () => {
      reset();
      setSelectedNode(null);
      setIsOpen(false);
    };

    const isLoading = renameNodeMutation.isPending;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          disabled={isLoading}
        >
          {t('documents.rename_modal.cancel')}
        </button>
        <button
          type="submit"
          form="rename-node-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
          disabled={isLoading}
        >
          {t('documents.rename_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.rename_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <form id="rename-node-form" onSubmit={handleFormSubmit(handleSubmit)} className="space-y-6">
          <FormInput
            label={t('documents.rename_modal.name.label')}
            type="text"
            placeholder={t('documents.rename_modal.name.placeholder')}
            error={errors.name?.message}
            required
            {...register('name', {
              required: t('documents.rename_modal.name.error.required'),
            })}
          />
        </form>
      </BaseModal>
    );
  },
);

RenameNodeModal.displayName = 'RenameNodeModal';
